import { zodResolver } from '@hookform/resolvers/zod';
import { ANALYTICS_EVENTS, posthog } from '@kiku/analytics/client';
import { createApplicationInputSchema } from '@kiku/candidates/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

type FormValues = z.infer<typeof createApplicationInputSchema>;

type CreateApplicationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  deptId: string;
  jobId: string;
};

export function CreateApplicationDialog({
  open,
  onOpenChange,
  orgId,
  deptId,
  jobId
}: CreateApplicationDialogProps): React.ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(createApplicationInputSchema)
  });

  const createApplication = useMutation({
    mutationFn: async (data: FormValues) => {
      const res = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications.$post({
        param: { orgId, deptId, jobId },
        json: data
      });
      if (!res.ok) {
        const body = (await res.json()) as { error?: string };
        throw new Error(body.error ?? i18n.t('errors.unexpected'));
      }
    },
    onSuccess: async () => {
      posthog.capture(ANALYTICS_EVENTS.INTERVIEW_SENT);
      await queryClient.invalidateQueries({ queryKey: ['applications', orgId, deptId, jobId] });
      reset();
      onOpenChange(false);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('applications.createDialog.title')}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            void handleSubmit((data) => {
              createApplication.mutate(data);
            })(e);
          }}
          className="flex flex-col gap-4 py-2"
        >
          <div className="flex flex-col gap-1.5">
            <Label>{t('applications.createDialog.name')}</Label>
            <Input
              {...register('name')}
              placeholder={t('applications.createDialog.namePlaceholder')}
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t('applications.createDialog.email')}</Label>
            <Input
              type="email"
              {...register('email')}
              placeholder={t('applications.createDialog.emailPlaceholder')}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>

          <div className="flex gap-3">
            <div className="flex w-28 flex-col gap-1.5">
              <Label>{t('applications.createDialog.phoneCountryCode')}</Label>
              <Input
                {...register('phoneCountryCode')}
                placeholder={t('applications.createDialog.phoneCountryCodePlaceholder')}
              />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label>{t('applications.createDialog.phoneNumber')}</Label>
              <Input
                {...register('phoneNumber')}
                placeholder={t('applications.createDialog.phoneNumberPlaceholder')}
              />
            </div>
          </div>

          {createApplication.isError && (
            <p className="text-xs text-destructive">{createApplication.error.message}</p>
          )}

          <DialogFooter>
            <Button type="submit" disabled={createApplication.isPending}>
              {t('applications.createDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
